import React from 'react';
import { Autocomplete, IconButton, TextField, Tooltip } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import styles from './MealPlanPopup.module.css';
import { MAX_SUBSTITUTES_PER_ITEM, UNIT_SUGGESTIONS, createSubstitute } from '../../pages/appointment/components/mealPlan/mealPlanModel';

const QUIET_INPUT = { disableUnderline: true };

/** Campo de unidade: texto livre com sugestões das medidas mais comuns. */
const UnitField = ({ value, onChange, className, label }) => (
  <Autocomplete
    freeSolo
    disableClearable
    forcePopupIcon={false}
    size="small"
    options={UNIT_SUGGESTIONS}
    value={value || ''}
    inputValue={value || ''}
    onInputChange={(e, newValue) => onChange(newValue || '')}
    className={className}
    renderInput={(params) => (
      <TextField
        {...params}
        variant="standard"
        placeholder="unidade"
        InputProps={{ ...params.InputProps, disableUnderline: true }}
        inputProps={{ ...params.inputProps, maxLength: 40, 'aria-label': label }}
      />
    )}
  />
);

/** Alimento, quantidade e unidade: a mesma trinca no item e na substituição. */
const FoodFields = ({ value, onChange, fieldClass, foodPlaceholder, labelPrefix }) => (
  <>
    <TextField
      variant="standard"
      placeholder={foodPlaceholder}
      value={value.food}
      onChange={(e) => onChange({ food: e.target.value })}
      className={`${styles.quiet} ${fieldClass}`}
      InputProps={QUIET_INPUT}
      inputProps={{ maxLength: 120, 'aria-label': `${labelPrefix}: alimento` }}
    />
    <TextField
      variant="standard"
      placeholder="Qtd."
      value={value.portion}
      onChange={(e) => onChange({ portion: e.target.value })}
      className={`${styles.quiet} ${fieldClass} ${styles.qtyField}`}
      InputProps={QUIET_INPUT}
      inputProps={{ maxLength: 20, inputMode: 'decimal', 'aria-label': `${labelPrefix}: quantidade` }}
    />
    <UnitField
      value={value.unit}
      onChange={(unit) => onChange({ unit })}
      className={`${styles.quiet} ${fieldClass}`}
      label={`${labelPrefix}: unidade`}
    />
  </>
);

/**
 * Um item da refeição no editor: uma linha da tabela (alimento, quantidade,
 * unidade) com ações que só aparecem ao passar o mouse (mover para cima ou
 * para baixo, observação, substituição, remover); observação e substituições
 * ficam recuadas abaixo, agrupadas por uma borda à esquerda.
 */
const MealItemEditor = ({ item, canMoveUp, canMoveDown, onMoveUp, onMoveDown, onChange, onRemove }) => {
  const update = (changes) => onChange({ ...item, ...changes });
  const showNote = !!(item.noteOpen || item.note);
  const canAddSubstitute = item.substitutes.length < MAX_SUBSTITUTES_PER_ITEM;

  function updateSubstitute(key, changes) {
    update({ substitutes: item.substitutes.map((sub) => (sub.key === key ? { ...sub, ...changes } : sub)) });
  }

  function removeSubstitute(key) {
    update({ substitutes: item.substitutes.filter((sub) => sub.key !== key) });
  }

  function addSubstitute() {
    if (!canAddSubstitute) return;
    update({ substitutes: [...item.substitutes, createSubstitute()] });
  }

  const hasExtras = showNote || item.substitutes.length > 0;

  return (
    <div className={styles.item}>
      <div className={`${styles.itemGrid} ${styles.itemRow}`}>
        <FoodFields
          value={item}
          onChange={update}
          fieldClass={styles.itemField}
          foodPlaceholder="Alimento"
          labelPrefix="Item"
        />
        <div className={styles.itemActions}>
          <Tooltip title="Mover para cima">
            <span>
              <IconButton size="small" onClick={onMoveUp} disabled={!canMoveUp}>
                <ArrowUpwardIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Mover para baixo">
            <span>
              <IconButton size="small" onClick={onMoveDown} disabled={!canMoveDown}>
                <ArrowDownwardIcon />
              </IconButton>
            </span>
          </Tooltip>
          {!showNote && (
            <Tooltip title="Observação">
              <IconButton size="small" onClick={() => update({ noteOpen: true })}>
                <NotesOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Substituição">
            <span>
              <IconButton size="small" onClick={addSubstitute} disabled={!canAddSubstitute}>
                <SwapHorizOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remover">
            <IconButton size="small" onClick={onRemove}>
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {hasExtras && (
        <div className={styles.itemExtras}>
          {showNote && (
            <TextField
              variant="standard"
              placeholder="Observação (ex.: sem açúcar)"
              value={item.note}
              onChange={(e) => update({ note: e.target.value })}
              className={`${styles.quiet} ${styles.itemNoteField}`}
              InputProps={QUIET_INPUT}
              inputProps={{ maxLength: 200, 'aria-label': 'Observação do item' }}
              autoFocus={!!item.noteOpen && !item.note}
              fullWidth
            />
          )}
          {item.substitutes.map((sub) => (
            <div key={sub.key} className={styles.substituteRow}>
              <span className={styles.substituteTag}>ou</span>
              <FoodFields
                value={sub}
                onChange={(changes) => updateSubstitute(sub.key, changes)}
                fieldClass={styles.substituteField}
                foodPlaceholder="Substituição"
                labelPrefix="Substituição"
              />
              <div className={styles.itemActions}>
                <Tooltip title="Remover substituição">
                  <IconButton size="small" onClick={() => removeSubstitute(sub.key)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MealItemEditor;
